import React, { useState } from "react"

import { Form, Formik } from "formik"
import { NotificationManager } from "react-notifications"
import { gql, useMutation } from "urql"
import * as Yup from "yup"

import {
  EllipsisHorizontalIcon,
  UserCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid"

import { Color, Size } from "~tailwindui/types/enums"

import { Button } from "~tailwindui/Basics"
import {
  AutocompleteSelect,
  Failures,
  SubmitButton,
  handleFailure,
} from "~tailwindui/Form"
import Modal from "~tailwindui/Modal"

import useIsMobile from "hooks/useIsMobile"

import Avatar from "components/Messaging/Avatar"

import { User } from "./"

export type ManageUsersModalProps = {
  users: User[]
  messageThreadId: string
}

const ManageUsersModal: React.FC<ManageUsersModalProps> = ({
  messageThreadId,
  users,
}) => {
  const isMobile = useIsMobile(1024)
  const [open, setOpen] = useState(false)

  const formState = {
    messageThreadId,
    userId: "",
  }

  const [, removeMutation]: any[] = useMutation(removeUserMutation)

  const [, runMutation]: any[] = useMutation(addUserMutation)

  const ValidationSchema = Yup.object().shape({
    userId: Yup.string().required("Please select a user"),
  })

  const addUser = (values, actions) => {
    return runMutation(values)
      .then(
        result => {
          const { failures } = result.data.messageThreadAddUser
          if (failures.length > 0) {
            handleFailure(actions, failures)
          } else {
            NotificationManager.info("User added")
          }
        },
        () => handleFailure(actions)
      )
      .catch(() => handleFailure(actions))
      .finally(() => actions.setSubmitting(false))
  }

  const removeUser = (id: string) => {
    removeMutation({ messageThreadId, userId: id })
      .then(
        result => {
          const { failures } = result.data.messageThreadRemoveUser
          if (failures.length > 0) {
            NotificationManager.error(
              `Failed to remove user: ${failures[0]["message"]}`
            )
          } else {
            NotificationManager.info("User removed")
          }
        },
        () => NotificationManager("Something went wrong")
      )
      .catch(() => NotificationManager("Something went wrong"))
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex min-w-max -space-x-2 md:-space-x-3"
      >
        <label className="sr-only">Members</label>
        {users.map(user => (
          <div key={user.id}>
            <Avatar
              size={isMobile ? "small" : "medium"}
              alt={user.messagingDisplayName}
              src={user.avatar.thumbUrl}
            />
          </div>
        ))}
        <div
          className="m-0 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-0 bg-gray-300 p-0 md:h-16 md:w-16"
          onClick={() => setOpen(true)}
        >
          <EllipsisHorizontalIcon className="m-0 h-6 w-6 p-0 text-white md:h-12 md:w-12" />
        </div>
      </button>

      <Modal.Dialog hideButton isOpen={open} closeModal={() => setOpen(false)}>
        <Formik
          initialValues={formState}
          onSubmit={addUser}
          validationSchema={ValidationSchema}
        >
          <Form>
            <Modal.Header Icon={UserCircleIcon} iconClassNames="text-blue-600">
              Add/Remove Participants
            </Modal.Header>
            <Modal.Body>
              <ul className="mb-4 space-y-4">
                {users.map(user => (
                  <li
                    key={user.id}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center space-x-2 md:space-x-4">
                      <Avatar
                        size="medium"
                        alt={user.messagingDisplayName}
                        src={user.avatar.thumbUrl}
                      />
                      <span className="text-lg">
                        {user.messagingDisplayName}
                      </span>
                    </div>
                    <button type="button" onClick={() => removeUser(user.id)}>
                      <XMarkIcon className="h-8 w-8 text-gray-500" />
                    </button>
                  </li>
                ))}
              </ul>

              <div className="my-6 flex items-center justify-between">
                <AutocompleteSelect
                  label=""
                  name="userId"
                  resource="messageableUsers"
                  searchColumn="fullName"
                  portal
                />
                <SubmitButton
                  text="Add User"
                  size={Size.Small}
                  color={Color.Teal}
                />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="flex w-full flex-col space-y-4">
                <Failures />
                <div className="flex w-full justify-end">
                  <Button onClick={() => setOpen(false)}>Done</Button>
                </div>
              </div>
            </Modal.Footer>
          </Form>
        </Formik>
      </Modal.Dialog>
    </>
  )
}

const addUserMutation = gql`
  mutation AddUser($messageThreadId: ID!, $userId: ID!) {
    messageThreadAddUser(messageThreadId: $messageThreadId, userId: $userId) {
      failures {
        message
      }
      messageThread {
        id
      }
    }
  }
`

const removeUserMutation = gql`
  mutation RemoveUser($messageThreadId: ID!, $userId: ID!) {
    messageThreadRemoveUser(
      messageThreadId: $messageThreadId
      userId: $userId
    ) {
      failures {
        message
      }
      messageThread {
        id
      }
    }
  }
`

export default ManageUsersModal
